import { createContext, useEffect, useState } from 'react';

export const NetworkContext = createContext<{ isOnline: boolean }>({ isOnline: true });

interface NetworkProviderProps {
  children: React.ReactNode;
}

interface NetworkListener {
  onOnline?: () => void;
  onOffline?: () => void;
}

const listeners = new Set<NetworkListener>();

export const registerNetworkListener = (listener: NetworkListener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export function NetworkProvider({ children }: NetworkProviderProps) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const updateStatus = () => {
      const online = navigator.onLine;
      setIsOnline((prev) => {
        if (prev !== online) {
          for (const listener of listeners) {
            if (online) {
              listener.onOnline?.();
            } else {
              listener.onOffline?.();
            }
          }
        }
        return prev;
      });
    };

    window.addEventListener('online', updateStatus);
    window.addEventListener('offline', updateStatus);

    return () => {
      window.removeEventListener('online', updateStatus);
      window.removeEventListener('offline', updateStatus);
    };
  }, []);

  return <NetworkContext.Provider value={{ isOnline }}>{children}</NetworkContext.Provider>;
}
